/**
 * Greater Sydney browse-map boundary.
 *
 * This intentionally uses a simple rectangular map restriction rather than an
 * administrative polygon. It covers the Sydney metro area from the Blue
 * Mountains through the coast, and from the Hawkesbury area down through the
 * Macarthur/Wollondilly area, while preventing the marketplace map from being
 * panned around the rest of Australia.
 */
export const GREATER_SYDNEY_MAP_BOUNDS = {
  north: -33.25,
  east: 151.45,
  south: -34.3,
  west: 150.2,
};

export const GREATER_SYDNEY_MAP_CENTER = {
  lat: -33.87,
  lng: 150.95,
};

export const GREATER_SYDNEY_MIN_ZOOM = 8;

const greaterSydneyBoundsAsSDKShape = () => ({
  ne: {
    lat: GREATER_SYDNEY_MAP_BOUNDS.north,
    lng: GREATER_SYDNEY_MAP_BOUNDS.east,
  },
  sw: {
    lat: GREATER_SYDNEY_MAP_BOUNDS.south,
    lng: GREATER_SYDNEY_MAP_BOUNDS.west,
  },
});

/**
 * Return true when a coordinate sits inside the Greater Sydney map rectangle.
 */
export const isWithinGreaterSydney = geolocation => {
  if (!geolocation) {
    return false;
  }

  const { lat, lng } = geolocation;
  const { north, east, south, west } = GREATER_SYDNEY_MAP_BOUNDS;

  return lat <= north && lat >= south && lng >= west && lng <= east;
};

/**
 * Clamp any requested search viewport to Greater Sydney. If the requested
 * viewport is entirely outside Sydney (or absent), fall back to the full
 * Greater Sydney viewport instead of allowing the map to jump elsewhere.
 */
export const limitBoundsToGreaterSydney = bounds => {
  const fullSydneyBounds = greaterSydneyBoundsAsSDKShape();
  const { ne, sw } = bounds || {};

  if (!ne || !sw) {
    return fullSydneyBounds;
  }

  const limited = {
    ne: {
      lat: Math.min(ne.lat, GREATER_SYDNEY_MAP_BOUNDS.north),
      lng: Math.min(ne.lng, GREATER_SYDNEY_MAP_BOUNDS.east),
    },
    sw: {
      lat: Math.max(sw.lat, GREATER_SYDNEY_MAP_BOUNDS.south),
      lng: Math.max(sw.lng, GREATER_SYDNEY_MAP_BOUNDS.west),
    },
  };

  const hasLatitudeOverlap = limited.ne.lat > limited.sw.lat;
  const hasLongitudeOverlap = limited.ne.lng > limited.sw.lng;

  return hasLatitudeOverlap && hasLongitudeOverlap ? limited : fullSydneyBounds;
};

/**
 * hasParentWithClassName searches class name from parent elements of given target
 * @param {Node} target - element whose parent might contain given class.
 * @param {String} className - class name string to be found
 */
export const hasParentWithClassName = (target, className) => {
  return [...document.querySelectorAll(`.${className}`)].some(
    el => el !== target && el.contains(target)
  );
};

/**
 * Build a stable cache key for fuzzy map coordinates. Listings from the same
 * artist at the same real location share a key so they resolve to one map
 * point. A different artist or a genuinely different location stays separate.
 *
 * @param {Object} listing - listing entity
 * @return {string|null} cache key for obfuscatedCoordinates
 */
export const getMapObfuscationCacheKey = listing => {
  const listingId = listing?.id?.uuid;
  const authorId = listing?.author?.id?.uuid;
  const geolocation = listing?.attributes?.geolocation;
  const identity = authorId || listingId;

  return identity && geolocation
    ? `${identity}_${geolocation.lat}_${geolocation.lng}`
    : null;
};

/**
 * Return true when a multi-listing map group belongs to one artist.
 * Single listings deliberately return false so the standard info card stays in use.
 */
export const listingsShareAuthor = listings => {
  if (!Array.isArray(listings) || listings.length < 2) {
    return false;
  }

  const firstAuthorId = listings[0]?.author?.id?.uuid;
  return !!firstAuthorId && listings.every(listing => listing?.author?.id?.uuid === firstAuthorId);
};

/**
 * Position the info card from its own dimensions instead of relying on the
 * zero-sized Google Maps OverlayView container. The card's caret then points
 * directly to the marker while the whole card stays above it.
 */
export const getInfoCardOverlayStyle = () => ({
  position: 'absolute',
  left: 0,
  bottom: 3,
  transform: 'translateX(-50%)',
});

/**
 * Listings array grouped by geolocation
 * @param {Array} mapListings - listings to be grouped on map
 * @return {Object} - Object where coordinate pair is the key to different listings
 */
export const groupedByCoordinates = mapListings => {
  return mapListings.reduce((acc, l) => {
    const g = l.attributes.geolocation;
    const key = `${g.lat}-${g.lng}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(l);
    return acc;
  }, {});
};

/**
 * Listings (in location based object literal) is mapped to array
 * @param {Object} mapListings - listings to be grouped on map
 * @return {Array} - An array where items are arrays of listings
 *   (They are arrays containing all the listings in that location)
 */
export const reducedToArray = mapListings => {
  return Object.values(mapListings).reduce((acc, listing) => acc.concat([listing]), []);
};
